import {
	buildUnauthorizedContext,
	getAuthToken,
	unauthorizedError$,
} from '@mopla/business-logic';
import { moplaClient } from '@mopla/data-models';
import { Logger } from '@mopla/logger';

let isHandling401 = false;

const handle401UnauthorizedError = async (error: unknown): Promise<void> => {
	// Prevent multiple simultaneous logout attempts
	if (isHandling401) {
		return;
	}

	isHandling401 = true;

	const logger = Logger.getInstance('Auth');

	try {
		// Emit the request context to trigger the 401 handling effect
		unauthorizedError$.next(buildUnauthorizedContext(error));
	} catch (emitError: unknown) {
		logger.error('Failed to emit unauthorized event', emitError);
	} finally {
		// Reset flag after a delay to allow for potential retries
		setTimeout(() => {
			isHandling401 = false;
		}, 1000);
	}
};

/**
 * Configure the HeyAPI client with Firebase authentication and Sentry integration
 * @param baseURL - Base URL for API requests
 */
export const configureMoplaClient = (baseURL: string) => {
	const logger = Logger.getInstance('Auth');

	// Set base URL and configure auth using hey-api's built-in mechanism
	moplaClient.setConfig({
		baseURL: baseURL,
		headers: {
			Accept: 'application/json, text/plain, */*',
		},
		auth: async () => {
			let token;

			try {
				token = await getAuthToken();
			} catch (e: unknown) {
				logger.error('Error getting auth token: ', e);
			}

			// hey-api will automatically add "Bearer" prefix due to bearer scheme in security config
			return token ?? undefined;
		},
	});
	moplaClient.instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			if (error.response?.status === 401) {
				await handle401UnauthorizedError(error);
			}

			return Promise.reject(error);
		}
	);
};
